import { useEffect, useRef } from 'react';
import { useAuthStore } from '@store/authStore';

export const LAST_ACTIVE_KEY = 'bcrip_last_active_ts';
export const DEFAULT_IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Custom hook that monitors user activity across tabs and automatically logs out
 * if the user has been inactive for more than 30 minutes in all tabs.
 */
export function useIdleTimer(timeoutMs: number = DEFAULT_IDLE_TIMEOUT_MS) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const lastWriteRef = useRef<number>(0);

  useEffect(() => {
    if (!isAuthenticated) return;

    // Record initial activity timestamp if none exists
    if (!localStorage.getItem(LAST_ACTIVE_KEY)) {
      localStorage.setItem(LAST_ACTIVE_KEY, Date.now().toString());
    }

    // Throttled activity updater (max 1 write per 2 seconds to avoid localStorage thrashing)
    const handleActivity = () => {
      const now = Date.now();
      if (now - lastWriteRef.current > 2000) {
        lastWriteRef.current = now;
        localStorage.setItem(LAST_ACTIVE_KEY, now.toString());
      }
    };

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach((evt) => {
      window.addEventListener(evt, handleActivity, { passive: true });
    });

    // Also update activity when user switches back to this tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleActivity();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Heartbeat check every 15 seconds across all tabs
    const checkInterval = setInterval(() => {
      const stored = localStorage.getItem(LAST_ACTIVE_KEY);
      const lastActive = stored ? parseInt(stored, 10) : Date.now();
      const now = Date.now();

      if (now - lastActive >= timeoutMs) {
        localStorage.removeItem(LAST_ACTIVE_KEY);
        useAuthStore.getState().logout();

        if (window.location.pathname !== '/login') {
          window.location.assign('/login?reason=idle_timeout');
        }
      }
    }, 15 * 1000);

    return () => {
      events.forEach((evt) => {
        window.removeEventListener(evt, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(checkInterval);
    };
  }, [isAuthenticated, timeoutMs]);
}
